import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useEditSession, usePaymentStatusEnum, useSessionEditHistory, useSessionStatusEnum } from '@/api/hooks'
import { toast } from '@/store/useToastStore'
import { Modal } from '@/shared/ui/Modal'
import { Input, Select, Textarea } from '@/shared/ui/Input'
import { cn } from '@/shared/utils'
import { AlertTriangle, ArrowRight, Calendar, ChevronDown, Clock, History, Hourglass, User } from 'lucide-react'

// ── Date helpers ──────────────────────────────────────────────────────────────
// The API speaks 'YYYY-MM-DD HH:mm:ss' in local (UAE) wall-clock time, so we
// parse/format the string parts directly instead of going through Date+ISO,
// which would shift everything by the browser's UTC offset.
function splitDT(v?: string | null): { date: string; time: string } {
  if (!v) return { date: '', time: '' }
  const m = String(v).match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/)
  if (m) return { date: m[1], time: m[2] }
  const d = new Date(v)
  if (isNaN(d.getTime())) return { date: '', time: '' }
  const p = (n: number) => String(n).padStart(2, '0')
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, time: `${p(d.getHours())}:${p(d.getMinutes())}` }
}
const joinDT = (date: string, time: string) => `${date} ${time}:00`
const minutesOf = (time: string) => {
  const [h, m] = time.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}
function fmtDuration(mins: number) {
  if (mins <= 0) return '—'
  const h = Math.floor(mins / 60), m = mins % 60
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ')
}
function prettyDT(date: string, time: string) {
  if (!date || !time) return '—'
  const d = new Date(`${date}T${time}`)
  if (isNaN(d.getTime())) return `${date} ${time}`
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
}

// ── Enum plumbing ─────────────────────────────────────────────────────────────
// The enum endpoints answer { status, values: [{ name, value }] } — note `value`,
// not `id`, and PENDING is 0, so every check here has to be null-safe rather than
// truthy-safe or the first option silently disappears.
interface EnumOption { value: number; name: string }

function readEnum(raw: any): EnumOption[] {
  const list = raw?.values ?? raw?.data ?? raw
  if (!Array.isArray(list)) return []
  return list
    .map((s: any) => ({ value: Number(s.value ?? s.id), name: String(s.name ?? s.label ?? '') }))
    .filter((s: EnumOption) => !isNaN(s.value) && !!s.name)
}

const normalizeName = (s: string) => s.toUpperCase().replace(/[\s_-]/g, '')

// Fallback only for when the enum endpoint itself fails — kept in sync with
// public/enum/get-session-status-type-enum.
const FALLBACK_STATUSES: EnumOption[] = [
  { value: 0, name: 'PENDING' },
  { value: 1, name: 'CONFIRMED' },
  { value: 2, name: 'CANCELLED' },
  { value: 3, name: 'COMPLETED' },
  { value: 4, name: 'IN PROGRESS' },
]

// ── Edit history ──────────────────────────────────────────────────────────────
// GET admin/booking/session/edit-history?session_id= → data: [{ id, changes: {field: {old,new}},
// reason, created_at, edited_by_name, edited_by_type }]
interface HistoryEntry {
  id: number; booking_id?: number; booking_session_id?: number
  changes?: Record<string, { old?: any; new?: any }>
  reason?: string | null; created_at?: string | null
  edited_by?: number | null; edited_by_name?: string | null; edited_by_type?: string | null
}

// The API sends raw column names — render them as human labels where we know one.
function fieldLabel(t: any, key: string) {
  const map: Record<string, string> = {
    start_time: t('editSession.startTime'),
    end_time: t('editSession.endTime'),
    session_status: t('editSession.status'),
  }
  return map[key] ?? key.replace(/_/g, ' ')
}

// Status columns are stored as numeric codes, so the history sends `1` where the
// admin expects "CONFIRMED". `options` is the live enum for that column — the
// label is looked up, never hardcoded, so new server-side statuses just appear.
function fieldValue(v: any, options?: EnumOption[]) {
  if (v == null || v === '') return '—'
  const s = String(v)
  if (options?.length && /^\d+$/.test(s)) {
    const hit = options.find(o => o.value === Number(s))
    if (hit) return hit.name
  }
  const m = s.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/)
  if (!m) return s
  return prettyDT(m[1], m[2])
}

export function SessionEditHistory({ sessionId, enabled, enums }: {
  sessionId?: number; enabled: boolean
  // field name → enum options, so numeric codes in `changes` render as names
  enums?: Record<string, EnumOption[]>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError } = useSessionEditHistory(sessionId, enabled)
  const entries: HistoryEntry[] = Array.isArray((data as any)?.data) ? (data as any).data : []
  // Newest first — the API returns them in insertion order.
  const sorted = [...entries].sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-bold text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <History size={13} className="text-gray-400" />
        <span className="flex-1 text-start">{t('editSession.historyTitle')}</span>
        {!isLoading && !isError && (
          <span className="px-1.5 py-0.5 rounded-full bg-white border border-gray-200 text-[10px] text-gray-500">{sorted.length}</span>
        )}
        <ChevronDown size={13} className={cn('text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
          {isLoading ? (
            <p className="px-3 py-3 text-xs text-gray-400">{t('editSession.historyLoading')}</p>
          ) : isError ? (
            <p className="px-3 py-3 text-xs text-red-500">{t('editSession.historyFailed')}</p>
          ) : sorted.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-400">{t('editSession.historyEmpty')}</p>
          ) : sorted.map(entry => (
            <div key={entry.id} className="px-3 py-2.5 space-y-1.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                  <User size={11} className="text-gray-400" />
                  {entry.edited_by_name ?? `#${entry.edited_by ?? '—'}`}
                  {entry.edited_by_type && (
                    <span className="px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-600 text-[10px] font-bold">{entry.edited_by_type}</span>
                  )}
                </span>
                <span className="text-[11px] text-gray-400">{fieldValue(entry.created_at)}</span>
              </div>

              <div className="space-y-1">
                {Object.entries(entry.changes ?? {}).map(([key, diff]) => {
                  const opts = enums?.[key]
                  return (
                    <div key={key} className="flex items-center gap-1.5 text-[11px] flex-wrap">
                      <span className="text-gray-400 w-20 flex-shrink-0 capitalize">{fieldLabel(t, key)}</span>
                      <span className="text-gray-400 line-through">{fieldValue(diff?.old, opts)}</span>
                      <ArrowRight size={11} className="text-gray-300 flex-shrink-0 rtl:rotate-180" />
                      <span className="font-semibold text-gray-800">{fieldValue(diff?.new, opts)}</span>
                    </div>
                  )
                })}
              </div>

              {entry.reason && (
                <p className="text-[11px] text-gray-500 italic bg-gray-50 rounded-md px-2 py-1">“{entry.reason}”</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Edit a single session: POST admin/booking/session/edit
// { session_id, start_time, end_time, session_status, reason }
export function EditSessionModal({ open, onClose, session, bookingId }: {
  open: boolean; onClose: () => void; session: any | null; bookingId?: number | string
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const editSession = useEditSession()
  const { data: enumData, isLoading: enumLoading } = useSessionStatusEnum(open)
  const { data: payEnumData } = usePaymentStatusEnum(open)

  const statuses: EnumOption[] = useMemo(() => {
    const mapped = readEnum(enumData)
    return mapped.length ? mapped : FALLBACK_STATUSES
  }, [enumData])

  // Both status columns can show up in the edit history as bare numbers.
  const historyEnums = useMemo(() => ({
    session_status: statuses,
    payment_status: readEnum(payEnumData),
  }), [statuses, payEnumData])

  const orig = useMemo(() => {
    const s = splitDT(session?.start_time)
    const e = splitDT(session?.end_time)
    return { date: s.date, start: s.time, end: e.time, status: String(session?.session_status ?? '').toUpperCase() }
  }, [session])

  const [date, setDate]     = useState(orig.date)
  const [start, setStart]   = useState(orig.start)
  const [end, setEnd]       = useState(orig.end)
  const [statusId, setStatusId] = useState('')
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)

  // Re-seed the form whenever a different session is opened.
  useEffect(() => {
    if (!open) return
    setDate(orig.date); setStart(orig.start); setEnd(orig.end)
    setReason(''); setConfirming(false)
  }, [open, orig])

  // Preselect the session's current status once the enum has resolved.
  useEffect(() => {
    if (!open || !statuses.length) return
    const match = statuses.find(s => normalizeName(s.name) === normalizeName(orig.status))
    setStatusId(match ? String(match.value) : '')
  }, [open, statuses, orig.status])

  const duration = start && end ? minutesOf(end) - minutesOf(start) : 0
  const selectedStatus = statuses.find(s => String(s.value) === statusId)
  const dirty =
    date !== orig.date || start !== orig.start || end !== orig.end ||
    (selectedStatus ? normalizeName(selectedStatus.name) !== normalizeName(orig.status) : false)

  // ── Validation ──────────────────────────────────────────────────────────────
  const errors: Record<string, string> = {}
  if (!date) errors.date = t('editSession.errDate')
  if (!start) errors.start = t('editSession.errStart')
  if (!end) errors.end = t('editSession.errEnd')
  if (date && start && end && duration <= 0) errors.end = t('editSession.errEndAfterStart')
  if (!reason.trim()) errors.reason = t('editSession.errReason')
  const isPast = date && start ? new Date(`${date}T${start}`).getTime() < Date.now() : false
  const valid = Object.keys(errors).length === 0 && !!statusId && dirty

  const handleClose = () => { setConfirming(false); onClose() }

  const submit = () => {
    editSession.mutate(
      {
        session_id: Number(session?.id),
        start_time: joinDT(date, start),
        end_time: joinDT(date, end),
        session_status: Number(statusId),
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast.success(t('editSession.toastSuccess'))
          if (bookingId) qc.invalidateQueries({ queryKey: ['booking-detail', String(bookingId)] })
          qc.invalidateQueries({ queryKey: ['booking-detail'] })
          qc.invalidateQueries({ queryKey: ['session-edit-history', Number(session?.id)] })
          handleClose()
        },
        onError: (e: any) => {
          toast.error(e?.response?.data?.message ?? t('editSession.toastFail'))
          setConfirming(false)
        },
      },
    )
  }

  if (!session) return null

  // ── Confirmation step ───────────────────────────────────────────────────────
  if (confirming) {
    const changes = [
      orig.date !== date && { label: t('editSession.date'), from: orig.date || '—', to: date },
      orig.start !== start && { label: t('editSession.startTime'), from: orig.start || '—', to: start },
      orig.end !== end && { label: t('editSession.endTime'), from: orig.end || '—', to: end },
      selectedStatus && normalizeName(selectedStatus.name) !== normalizeName(orig.status) &&
        { label: t('editSession.status'), from: orig.status || '—', to: selectedStatus.name },
    ].filter(Boolean) as { label: string; from: string; to: string }[]

    return (
      <Modal open={open} onClose={handleClose} title={t('editSession.confirmTitle')} size="sm">
        <div className="space-y-4">
          <div className="flex gap-3 p-3 rounded-xl bg-brand-50 border border-brand-100">
            <AlertTriangle size={18} className="text-brand-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              {t('editSession.confirmIntro')} <span className="font-bold">#{session.id}</span>
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
            {changes.map(c => (
              <div key={c.label} className="flex items-center gap-2 px-3 py-2.5 text-sm">
                <span className="text-xs text-gray-400 w-24 flex-shrink-0">{c.label}</span>
                <span className="text-gray-400 line-through truncate">{c.from}</span>
                <ArrowRight size={13} className="text-gray-300 flex-shrink-0 rtl:rotate-180" />
                <span className="font-bold text-brand-700 truncate">{c.to}</span>
              </div>
            ))}
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <span className="font-semibold text-gray-600">{t('editSession.reason')}:</span> {reason.trim()}
          </div>
          <p className="text-xs text-gray-400">{t('editSession.notifyWarning')}</p>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setConfirming(false)}
              disabled={editSession.isPending}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {t('editSession.back')}
            </button>
            <button
              onClick={submit}
              disabled={editSession.isPending}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
            >
              {editSession.isPending ? t('editSession.saving') : t('editSession.confirmSave')}
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  // ── Form step ───────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={handleClose} title={t('editSession.title')} size="md">
      <div className="space-y-4">
        {/* Current schedule, for reference while editing */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-500">
          <span className="font-semibold text-gray-700">{t('editSession.sessionHash')}{session.id}</span>
          <span className="flex items-center gap-1"><Calendar size={12} className="text-gray-400" />{orig.date || '—'}</span>
          <span className="flex items-center gap-1"><Clock size={12} className="text-gray-400" />{orig.start || '—'} – {orig.end || '—'}</span>
          {orig.status && <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 font-semibold text-gray-600">{orig.status}</span>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label={t('editSession.date')}
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            error={errors.date}
          />
          <Input
            label={t('editSession.startTime')}
            type="time"
            value={start}
            onChange={e => setStart(e.target.value)}
            error={errors.start}
          />
          <Input
            label={t('editSession.endTime')}
            type="time"
            value={end}
            onChange={e => setEnd(e.target.value)}
            error={errors.end}
          />
        </div>

        {/* Live preview of the resulting slot */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-brand-50 border border-brand-100">
          <span className="text-sm font-semibold text-brand-800">
            {prettyDT(date, start)}{end ? ` → ${end}` : ''}
          </span>
          <span className={cn('flex items-center gap-1 text-xs font-bold', duration > 0 ? 'text-brand-700' : 'text-red-500')}>
            <Hourglass size={12} />{fmtDuration(duration)}
          </span>
        </div>

        {isPast && (
          <p className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
            {t('editSession.pastWarning')}
          </p>
        )}

        <Select
          label={t('editSession.status')}
          value={statusId}
          onChange={e => setStatusId(e.target.value)}
          disabled={enumLoading}
        >
          <option value="">{enumLoading ? t('editSession.loadingStatuses') : t('editSession.selectStatus')}</option>
          {statuses.map(s => <option key={s.value} value={s.value}>{s.name}</option>)}
        </Select>

        <Textarea
          label={t('editSession.reason')}
          className="min-h-20"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('editSession.reasonPlaceholder')}
          error={reason.length > 0 ? errors.reason : undefined}
        />

        <SessionEditHistory sessionId={Number(session.id)} enabled={open} enums={historyEnums} />

        <div className="flex items-center justify-end gap-3">
          <button onClick={handleClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
            {t('editSession.cancel')}
          </button>
          <button
            onClick={() => setConfirming(true)}
            disabled={!valid}
            title={!dirty ? t('editSession.noChanges') : undefined}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('editSession.saveChanges')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
